/**
 * /admin/billing — plan & payment page for the agent.
 *
 * Shows current subscription state (free listing, active-until date, or
 * lapsed) and both plans as cards with one-click Stripe checkout. The annual
 * plan is highlighted as best value (2 months free + a locked rate).
 */
import React, { useState } from "react";
import { CheckCircle2, Sparkles, Loader2, AlertCircle, CalendarClock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBilling } from "../components/BillingPaywall";

const PLAN_META = {
  monthly: {
    title: "Monthly",
    price: "$49",
    per: "/ month",
    features: ["Everything in PorchLight", "Unlimited sellers", "Cancel any time"],
  },
  annual: {
    title: "Annual",
    price: "$490",
    per: "/ year",
    note: "2 free months.",
    features: ["Everything in Monthly", "2 months free", "Your rate locked for 12 months"],
    badge: "Best value",
  },
};

export default function AdminBilling() {
  const { user } = useAuth();
  const { status, loading } = useBilling(user);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  const checkout = async (packageId) => {
    setErr(""); setBusyId(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error("No checkout URL");
    } catch (e) {
      setErr(formatApiError(e));
      setBusyId(null);
    }
  };

  // Cancelling or swapping a card is the agent's own business. Stripe
  // hosts the screen for it, so there is nothing here to build or to
  // get wrong, and no email to Erika asking her to do it for them.
  const manage = async () => {
    setErr("");
    setBusyId("portal");
    try {
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error("No billing portal URL");
    } catch (e) {
      setErr(formatApiError(e));
      setBusyId(null);
    }
  };

  const periodEnd = status?.current_period_end ? new Date(status.current_period_end) : null;
  const statusLine = (() => {
    if (!status) return "";
    if (status.legacy) return "Your listings are unlimited on this account.";
    if (status.status === "trialing")
      return status.can_add_listing
        ? "Your first listing is free. Adding a second client is what starts your subscription."
        : "Your first listing is free. Adding another client starts your subscription.";
    if (status.active && periodEnd) {
      const on = periodEnd.toLocaleDateString(undefined, {
        month: "long", day: "numeric", year: "numeric",
      });
      if (status.cancel_at_period_end) return `Cancelled. Your access runs until ${on}.`;
      if (status.renews) return `Active. Renews by itself on ${on}.`;
      return `Active. Your access runs through ${on}.`;
    }
    if (status.active) return "Active subscription.";
    return "Your subscription has lapsed — pick a plan below to unlock your admin tools again. Nothing was deleted.";
  })();

  return (
    <div data-testid="admin-billing-page" style={{ maxWidth: 880, margin: "0 auto", padding: "40px 0 80px" }}>
      <div className="overline" style={{ color: "var(--secondary)" }}>Account</div>
      <h1 className="font-serif" style={{ fontSize: "clamp(1.8rem, 3vw, 2.4rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "8px 0 10px" }}>
        Billing & plan
      </h1>
      {loading || !status ? (
        <p style={{ color: "var(--text-muted)" }}>Loading your billing status…</p>
      ) : (
        <>
          <div data-testid="billing-status-line" className="card"
               style={{ padding: "16px 20px", marginBottom: 28, display: "flex", gap: 12, alignItems: "center",
                        background: "var(--surface-alt)" }}>
            {status.status === "trialing" ? <Sparkles size={18} color="var(--secondary)" /> : <CalendarClock size={18} color="var(--primary)" />}
            <span style={{ fontSize: 14, lineHeight: 1.5 }}>{statusLine}</span>
          {status.can_manage_billing && (
            <button
              data-testid="manage-billing"
              className="btn-secondary"
              style={{ marginLeft: "auto", whiteSpace: "nowrap" }}
              disabled={busyId === "portal"}
              onClick={manage}
            >
              {busyId === "portal" ? "Opening" : "Manage billing"}
            </button>
          )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {["monthly", "annual"].map((id) => {
              const meta = PLAN_META[id];
              const pkg = (status.packages || []).find((p) => p.id === id);
              const highlighted = id === "annual";
              return (
                <div key={id} data-testid={`billing-plan-${id}`} className="card"
                     style={{ padding: 28, position: "relative",
                              border: highlighted ? "2px solid var(--primary)" : "1px solid var(--border)" }}>
                  {meta.badge && (
                    <span style={{ position: "absolute", top: -12, right: 20, background: "var(--primary)", color: "#fff",
                                   fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
                                   padding: "4px 12px", borderRadius: 999 }}>
                      {meta.badge}
                    </span>
                  )}
                  <div className="overline">{meta.title}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "10px 0 4px" }}>
                    <span className="font-serif" style={{ fontSize: "2.2rem", fontWeight: 500 }}>{meta.price}</span>
                    <span style={{ color: "var(--text-muted)", fontSize: 14 }}>{meta.per}</span>
                  </div>
                  {meta.note ? <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "0 0 16px" }}>{meta.note}</p> : null}
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {meta.features.map((f) => (
                      <li key={f} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13 }}>
                        <CheckCircle2 size={15} color="var(--primary)" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`billing-subscribe-${id}`}
                    className={highlighted ? "btn-primary" : "btn-secondary"}
                    style={{ width: "100%", justifyContent: "center", opacity: busyId ? 0.6 : 1 }}
                    disabled={!!busyId}
                    onClick={() => checkout(id)}
                  >
                    {busyId === id
                      ? (<><Loader2 size={14} className="spin" /> Redirecting…</>)
                      : `Get ${meta.title.toLowerCase()} — ${meta.price}${id === "annual" ? "/yr" : "/mo"}`}
                  </button>
                  <p style={{ color: "var(--text-muted)", fontSize: 12, margin: "10px 0 0", textAlign: "center" }}>
                    Secure checkout via Stripe
                  </p>
                </div>
              );
            })}
          </div>
          {err && (
            <div data-testid="billing-error" style={{ marginTop: 18, fontSize: 14, color: "var(--error)", display: "flex", alignItems: "center", gap: 8 }}>
              <AlertCircle size={14} /> {err}
            </div>
          )}
        </>
      )}
    </div>
  );
}
